import { parseFinnishMobileNumberParts } from '@n2-common/validation/fi';
import { parseSwedishMobilePhoneNumber } from '@n2-common/validation/se';
import { type FormattedInputConfig } from '../Input/FormattedInput';
import { parseSwedishAccountNumber } from '@n2-common/validation/se/accountNumber';
import { parseSwedishSsn } from '@n2-common/validation/se/nationalId';
import { parseNorwegianMobilePhoneNumber } from '@n2-common/validation/no/phoneNumber';

export const AmountFormat: FormattedInputConfig = {
    cleanup(value: string) {
        return value.replace(/\D/g, '');
    },

    format(value?: string | number | readonly string[]): string {
        if (typeof value == 'string') {
            const amount = parseInt(value.replace(/\D/g, ''));
            return isNaN(amount) ? '' : amount.toLocaleString('nb-NO');
        }
        if (typeof value == 'number') {
            return value.toLocaleString('nb-NO');
        }
        return '';
    },
    inputMode: 'numeric',
};

// 19991212-0456
export const SwedishSsnFormat: FormattedInputConfig = {
    cleanup(value) {
        const parsed = parseSwedishSsn(value);
        if (parsed) {
            return parsed.structuredFormat;
        }
        return value;
    },
    format(value) {
        if (!value) {
            return '';
        }
        const parsed = parseSwedishSsn(`${value}`);
        if (parsed) {
            return parsed.officialFormat;
        }
        return `${value}`;
    },
    inputMode: 'numeric',
};

export const FinnishSsnFormat: FormattedInputConfig = {
    cleanup(value) {
        return value
            .toUpperCase()
            .replace(/[^\d+\-A-Y]/g, '')
            .slice(0, 11);
    },
    format(value) {
        return `${value}`
            .toUpperCase()
            .replace(/[^\d+\-A-Y]/g, '')
            .slice(0, 11);
    },
    inputMode: 'text',
};

// FI49 5000 9420 0287 30
export const FinnishAccountNumberFormat: FormattedInputConfig = {
    cleanup(value) {
        return value
            .toUpperCase()
            .replace(/[^\dFI]/g, '')
            .slice(0, 18);
    },
    format(value) {
        if (!value) {
            return '';
        }
        return `${value}`
            .toUpperCase()
            .replace(/[^\dFI]/g, '')
            .slice(0, 18);
    },
    inputMode: 'text',
};

export const NorwegianAccountNumberFormat: FormattedInputConfig = {
    cleanup(value) {
        return value.replace(/\D/g, '');
    },
    format(value) {
        if (!value) {
            return '';
        }
        const digits = `${value}`.replace(/\D/g, '');
        return digits && addSpacing(digits, 4, 6);
    },
    inputMode: 'text',
};

const ZERO_RE = /\(0\)/;
export const FinnishPhoneNumberFormat: FormattedInputConfig = {
    cleanup(value) {
        const { isValid, country, operator, subscriber } = parseFinnishMobileNumberParts(value.replace(ZERO_RE, ''));
        return isValid ? `${country}${operator?.slice(1)}${subscriber}` : value;
    },
    format(value) {
        const cleaned = `${value}`.replace(/^(?<countrycode>(\+|00)358)/, '').replace(/[^\d+]/g, '');
        const { operator, subscriber, isValid } = parseFinnishMobileNumberParts(cleaned);
        if (isValid) {
            return `${operator?.slice(1)} ${subscriber && [4, 5].includes(subscriber.length) ? subscriber : addSpacing(subscriber, 3, 5)}`;
        } else {
            const m = /^(457|4([^5]|5(?!7))|50)(\d*)$/.exec(cleaned);

            return m ? addSpacing(cleaned, m[1].length, m[1].length + 3, m[1].length + 5) : cleaned;
        }
    },
    inputMode: 'numeric',
    prefix: '+358 (0)',
};

export const SwedishPhoneNumberFormat: FormattedInputConfig = {
    cleanup(value) {
        const info = parseSwedishMobilePhoneNumber(value);
        return info.isValid ? `+46${info.network}${info.number}` : value;
    },
    format(value) {
        const cleaned = `${value}`.replace(/^((\+|00|0)46|0)/, '').replace(/[^\d+]/g, '');
        const info = parseSwedishMobilePhoneNumber(cleaned);
        if (info.isValid) {
            return `${info.network} ${addSpacing(info.number, 3, 5)}`;
        } else {
            return cleaned;
        }
    },
    inputMode: 'numeric',
    prefix: '+46',
};

export const SwedishAccountNumberFormat: FormattedInputConfig = {
    format(value) {
        const cleaned = `${value}`.replace(/[^\d-]/g, '');
        return cleaned;
    },
    cleanup(value) {
        return parseSwedishAccountNumber(value).formatted;
    },
};

export const NorwegianPhoneNumberFormat: FormattedInputConfig = {
    cleanup(value) {
        const info = parseNorwegianMobilePhoneNumber(value);
        return info.isValid ? `+47${info.network}${info.number}` : value;
    },
    format(value) {
        const cleaned = `${value}`.replace(/^((\+|00|0)47|0)/, '').replace(/[^\d+]/g, '');
        const info = parseNorwegianMobilePhoneNumber(cleaned);
        if (info.isValid) {
            return `${info.network} ${addSpacing(info.number, 3, 5)}`;
        } else {
            return cleaned;
        }
    },
    inputMode: 'numeric',
    prefix: '+47',
};
/**
 * @param number Adds a single space at provided indexes
 */
function addSpacing(number?: string, ...indexes: number[]) {
    return number
        ? number
              .split('')
              .map((c, i) => (indexes.includes(i) ? ` ${c}` : c))
              .join('')
        : '';
}
